<template>
  <div class="address-container">
    <!-- ========== 搜索 & 操作栏 ========== -->
    <el-card class="filter-card" shadow="never">
      <el-form :inline="true" class="filter-form">
        <el-form-item label="关键词">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索收货人、手机号或地址"
            clearable
            prefix-icon="el-icon-search"
            style="width:280px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-plus" @click="openAddAddress">
            新增地址
          </el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- ========== 地址列表表格 ========== -->
    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="filteredAddressList"
        border
        stripe
        height="68vh"
        style="width:100%"
        row-key="id"
      >
        <el-table-column prop="name" label="收货人" min-width="100" />
        <el-table-column prop="phone" label="手机号" width="130" />
        <el-table-column prop="fullAddress" label="地址" min-width="200" show-overflow-tooltip />
        <el-table-column label="默认" width="80" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.is_default" size="small" type="success" class="default-tag">
              默认
            </el-tag>
            <span v-else style="color:#ccc;">—</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openEditAddress(scope.row)">
              编辑
            </el-button>
            <el-button type="text" size="small" style="color:#F56C6C;" @click="confirmDelete(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="table-footer">
        <span>共 <span class="count">{{ filteredAddressList.length }}</span> 条地址</span>
      </div>
    </el-card>

    <!-- ========== 新增 / 编辑地址弹窗 ========== -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="560px"
      :close-on-click-modal="false"
      @close="closeDialog"
    >
      <el-form
        ref="addressForm"
        :model="formData"
        :rules="formRules"
        label-width="100px"
        class="address-form"
        size="small"
      >
        <el-form-item label="收货人" prop="name">
          <el-input v-model="formData.name" placeholder="请输入姓名" />
        </el-form-item>

        <el-form-item label="手机号码" prop="mobile">
          <el-input v-model="formData.mobile" placeholder="请输入手机号" />
        </el-form-item>

        <!-- 省市区三级联动（使用三个 el-select 级联） -->
        <el-form-item label="所在地区" prop="province_id" required>
          <div class="region-group">
            <el-select
              v-model="formData.province_id"
              placeholder="请选择省份"
              @change="onProvinceChange"
              :disabled="regionLoading"
            >
              <el-option
                v-for="p in provinces"
                :key="p.id"
                :label="p.name"
                :value="p.id"
              />
            </el-select>
            <el-select
              v-model="formData.city_id"
              placeholder="请选择城市"
              @change="onCityChange"
              :disabled="!formData.province_id || regionLoading"
            >
              <el-option
                v-for="c in cities"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              />
            </el-select>
            <el-select
              v-model="formData.district_id"
              placeholder="请选择区县"
              @change="onDistrictChange"
              :disabled="!formData.city_id || regionLoading"
            >
              <el-option
                v-for="d in districts"
                :key="d.id"
                :label="d.name"
                :value="d.id"
              />
            </el-select>
          </div>
          <!-- 显示完整地区文本（隐藏域） -->
          <el-input v-model="formData.area" type="hidden" />
        </el-form-item>

        <el-form-item label="详细地址" prop="detail_address">
          <el-input
            v-model="formData.detail_address"
            type="textarea"
            :rows="2"
            placeholder="街道、门牌号等"
          />
        </el-form-item>

        <el-form-item label="设为默认地址" prop="is_default">
          <el-switch v-model="formData.is_default" active-color="#07c160" />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitAddress">
          保存
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import xcxrequest from '@/utils/xcxrequest'

export default {
  name: 'AddressManagement',
  data () {
    return {
      // ---------- 列表 ----------
      loading: false,
      addressList: [], // 原始地址列表
      searchKeyword: '', // 搜索关键词

      // ---------- 弹窗 ----------
      dialogVisible: false,
      isEdit: false, // true=编辑, false=新增
      submitLoading: false,
      formData: {
        id: null,
        name: '',
        mobile: '',
        province_id: null,
        city_id: null,
        district_id: null,
        province: '',
        city: '',
        district: '',
        detail_address: '',
        is_default: false,
        area: '' // 用于显示完整地区（可选）
      },
      formRules: {
        name: [{ required: true, message: '请输入收货人姓名', trigger: 'blur' }],
        mobile: [
          { required: true, message: '请输入手机号码', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        province_id: [{ required: true, message: '请选择省份', trigger: 'change' }],
        city_id: [{ required: true, message: '请选择城市', trigger: 'change' }],
        district_id: [{ required: true, message: '请选择区县', trigger: 'change' }],
        detail_address: [{ required: true, message: '请输入详细地址', trigger: 'blur' }]
      },

      // ---------- 省市区数据 ----------
      provinces: [],
      cities: [],
      districts: [],
      regionLoading: false,

      // ---------- 用户信息 ----------
      userId: null
    }
  },

  computed: {
    dialogTitle () {
      return this.isEdit ? '编辑地址' : '新增地址'
    },
    // 过滤后的地址列表
    filteredAddressList () {
      if (!this.searchKeyword.trim()) return this.addressList
      const kw = this.searchKeyword.trim().toLowerCase()
      return this.addressList.filter(item => {
        return (
          item.name.toLowerCase().includes(kw) ||
          item.phone.includes(kw) ||
          item.fullAddress.toLowerCase().includes(kw)
        )
      })
    }
  },

  created () {
    this.userId = localStorage.getItem('crm_id') || '1' // 示例
    this.loadAddressList()
    this.loadProvinces()
  },

  methods: {
    // ==================== 加载地址列表 ====================
    async loadAddressList () {
      this.loading = true
      try {
        const res = await xcxrequest.post('/gouwu/address.php?a=sel', {
          user_id: this.userId
        })
        // 假设返回数据结构 { code: 0, data: [...] }
        this.addressList = (res.data || []).map(item => ({
          id: item.id,
          name: item.name,
          phone: item.mobile,
          province: item.province,
          city: item.city,
          district: item.district,
          detail: item.detail_address,
          fullAddress: `${item.province}${item.city}${item.district}${item.detail_address}`,
          is_default: item.is_default === '1',
          province_id: item.province_id || null,
          city_id: item.city_id || null,
          district_id: item.district_id || null
        }))
        // 排序：默认地址置顶
        this.addressList.sort((a, b) => (a.is_default === b.is_default ? 0 : a.is_default ? -1 : 1))
      } catch (error) {
        console.error('加载地址列表失败', error)
        this.$message.error('加载地址列表失败')
      } finally {
        this.loading = false
      }
    },

    // ==================== 搜索 / 重置 ====================
    handleSearch () {
      // 无需额外操作，computed 自动响应
    },
    handleReset () {
      this.searchKeyword = ''
    },

    // ==================== 新增 / 编辑 ====================
    openAddAddress () {
      this.isEdit = false
      this.resetFormData()
      this.dialogVisible = true
      // 清除校验状态
      this.$nextTick(() => {
        this.$refs.addressForm && this.$refs.addressForm.clearValidate()
      })
    },

    openEditAddress (row) {
      this.isEdit = true
      // 填充表单数据
      this.formData = {
        id: row.id,
        name: row.name,
        mobile: row.phone,
        province_id: row.province_id,
        city_id: row.city_id,
        district_id: row.district_id,
        province: row.province,
        city: row.city,
        district: row.district,
        detail_address: row.detail,
        is_default: row.is_default,
        area: `${row.province}${row.city}${row.district}`
      }
      // 同步省市区选择器的显示（根据ID回显）
      this.syncRegionForEdit()
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.addressForm && this.$refs.addressForm.clearValidate()
      })
    },

    resetFormData () {
      this.formData = {
        id: null,
        name: '',
        mobile: '',
        province_id: null,
        city_id: null,
        district_id: null,
        province: '',
        city: '',
        district: '',
        detail_address: '',
        is_default: false,
        area: ''
      }
      this.cities = []
      this.districts = []
    },

    closeDialog () {
      this.dialogVisible = false
      this.$refs.addressForm && this.$refs.addressForm.resetFields()
    },

    // ==================== 省市区三级联动 ====================
    async loadProvinces () {
      try {
        const res = await xcxrequest.post('/gouwu/address.php?a=provinces')
        if (res.code === '0' && res.data) {
          this.provinces = res.data
        }
      } catch (e) {
        console.error('加载省份失败', e)
      }
    },

    async loadCities (provinceId) {
      if (!provinceId) return
      try {
        const res = await xcxrequest.post('/gouwu/address.php?a=cities', {
          province_id: provinceId
        })
        if (res.code === '0' && res.data) {
          this.cities = res.data
        }
      } catch (e) {
        console.error('加载城市失败', e)
      }
    },

    async loadDistricts (cityId) {
      if (!cityId) return
      try {
        const res = await xcxrequest.post('/gouwu/address.php?a=districts', {
          city_id: cityId
        })
        if (res.code === '0' && res.data) {
          this.districts = res.data
        }
      } catch (e) {
        console.error('加载区县失败', e)
      }
    },

    // 省份变更
    onProvinceChange (val) {
      const province = this.provinces.find(p => p.id === val)
      if (province) {
        this.formData.province = province.name
        this.formData.city_id = null
        this.formData.district_id = null
        this.formData.city = ''
        this.formData.district = ''
        this.cities = []
        this.districts = []
        this.loadCities(val)
        this.updateArea()
      }
    },

    // 城市变更
    onCityChange (val) {
      const city = this.cities.find(c => c.id === val)
      if (city) {
        this.formData.city = city.name
        this.formData.district_id = null
        this.formData.district = ''
        this.districts = []
        this.loadDistricts(val)
        this.updateArea()
      }
    },

    // 区县变更
    onDistrictChange (val) {
      const district = this.districts.find(d => d.id === val)
      if (district) {
        this.formData.district = district.name
        this.updateArea()
      }
    },

    // 更新完整地区文本
    updateArea () {
      this.formData.area = `${this.formData.province}${this.formData.city}${this.formData.district}`
    },

    // 编辑时根据ID回显省市区选择
    async syncRegionForEdit () {
      // 如果没有ID则无法回显
      if (!this.formData.province_id) return
      this.regionLoading = true
      try {
        // 先加载省份列表（已加载），找到省份索引
        // 但不需索引，直接设置值即可触发 change
        // 但要确保 cities 和 districts 已加载对应数据
        // 先加载城市
        await this.loadCities(this.formData.province_id)
        // 再加载区县
        if (this.formData.city_id) {
          await this.loadDistricts(this.formData.city_id)
        }
        // 确保选中的值在选项中存在（无需额外操作，v-model 会匹配）
        // 但可能因为异步加载，需强制更新视图
        this.$forceUpdate()
      } catch (e) {
        console.error('回显省市区失败', e)
      } finally {
        this.regionLoading = false
      }
    },

    // ==================== 提交地址（新增 / 编辑） ====================
    async submitAddress () {
      this.$refs.addressForm.validate(async (valid) => {
        if (!valid) return

        // 校验省市区是否完整（由于级联，只要ID存在即可）
        if (!this.formData.province_id || !this.formData.city_id || !this.formData.district_id) {
          this.$message.warning('请完整选择省市区')
          return
        }

        this.submitLoading = true
        try {
          // 如果设为默认，先重置其他默认地址
        //   if (this.formData.is_default) {
        //     await this.resetOtherDefaultAddress(this.formData.id)
        //   }

          const baseParams = {
            name: this.formData.name,
            mobile: this.formData.mobile,
            province: this.formData.province,
            city: this.formData.city,
            district: this.formData.district,
            detail_address: this.formData.detail_address,
            is_default: this.formData.is_default ? '1' : '0',
            province_id: this.formData.province_id,
            city_id: this.formData.city_id,
            district_id: this.formData.district_id
          }

          let url, params
          if (this.isEdit && this.formData.id) {
            url = '/gouwu/address.php?a=edit'
            params = { id: this.formData.id, ...baseParams }
          } else {
            url = '/gouwu/address.php?a=address'
            params = { user_id: this.userId, ...baseParams }
          }

          const res = await xcxrequest.post(url, params)
          if (res.code === '0') {
            this.$message.success(this.isEdit ? '修改成功' : '添加成功')
            this.dialogVisible = false
            await this.loadAddressList()
          } else {
            this.$message.error(res.msg || '操作失败')
          }
        } catch (error) {
          console.error('保存地址失败', error)
          this.$message.error(error?.msg || '网络错误，请重试')
        } finally {
          this.submitLoading = false
        }
      })
    },

    // 重置其他默认地址
    async resetOtherDefaultAddress (excludeId = null) {
      const defaultItems = this.addressList.filter(
        addr => addr.is_default === true && (excludeId === null || addr.id !== excludeId)
      )
      for (const addr of defaultItems) {
        try {
          await xcxrequest.post('/gouwu/address.php?a=edit', {
            id: addr.id,
            name: addr.name,
            mobile: addr.phone,
            province: addr.province,
            city: addr.city,
            district: addr.district,
            detail_address: addr.detail,
            is_default: '0',
            province_id: addr.province_id,
            city_id: addr.city_id,
            district_id: addr.district_id
          })
        } catch (e) {
          console.error('重置默认地址失败', e)
        }
      }
    },

    // ==================== 删除 ====================
    confirmDelete (row) {
      this.$confirm(`确定删除收货人 ${row.name} 的地址吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await xcxrequest.post('/gouwu/address.php?a=del', { id: row.id })
          this.$message.success('删除成功')
          await this.loadAddressList()
        } catch (error) {
          console.error('删除失败', error)
          this.$message.error(error?.msg || '删除失败')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.address-container {
  padding: 0 20px 20px;
  background-color: #f0f2f6;
 height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;

  .filter-card {
    margin-bottom: 16px;
    .filter-form {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 0;
      .el-form-item {
        margin-bottom: 0;
        margin-right: 16px;
        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

  .table-card {
    .table-footer {
      margin-top: 16px;
      text-align: right;
      color: #909399;
      font-size: 13px;
      .count {
        color: #333;
        font-weight: 600;
      }
    }
  }

  .address-form {
    .region-group {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      .el-select {
        flex: 1;
        min-width: 130px;
      }
    }
    .switch-item {
      display: flex;
      align-items: center;
      .el-form-item__label {
        margin-bottom: 0;
      }
    }
  }
}
</style>
